import React, { useState } from "react";
import api from "../api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Edit3 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../auth";
import AddressAutocomplete from "./AddressAutocomplete";

/**
 * Lets any authenticated user suggest an edit to a pool entry.
 * Submitted to the admin queue and only applied after admin approval.
 *
 * Props:
 *  - pool: { pool_id, name, address, lat, lng, phone }
 *  - trigger: optional ReactNode (otherwise default outline button is rendered)
 */
export default function PoolSuggestEditDialog({ pool, trigger }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(pool?.name || "");
  const [address, setAddress] = useState(pool?.address || "");
  const [lat, setLat] = useState(pool?.lat ?? "");
  const [lng, setLng] = useState(pool?.lng ?? "");
  const [phone, setPhone] = useState(pool?.phone || "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  if (!pool) return null;

  const reset = () => {
    setName(pool.name || ""); setAddress(pool.address || "");
    setLat(pool.lat ?? ""); setLng(pool.lng ?? "");
    setPhone(pool.phone || ""); setReason("");
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!user) { toast.error("Connectez-vous pour suggérer une modification."); return; }
    const payload = {};
    if ((name || "").trim() && name.trim() !== (pool.name || "")) payload.name = name.trim();
    if ((address || "").trim() && address.trim() !== (pool.address || "")) payload.address = address.trim();
    if (lat !== "" && lat !== null && Number(lat) !== Number(pool.lat)) payload.lat = Number(lat);
    if (lng !== "" && lng !== null && Number(lng) !== Number(pool.lng)) payload.lng = Number(lng);
    if ((phone || "").trim() !== (pool.phone || "")) payload.phone = phone.trim();
    payload.reason = (reason || "").trim();
    if (Object.keys(payload).length <= 1) {
      toast.error("Aucune modification détectée.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/pools/${pool.pool_id}/suggest-edit`, payload);
      toast.success("Merci ! Votre suggestion a été envoyée à l'administrateur pour validation.");
      setOpen(false); reset();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi impossible");
    } finally { setBusy(false); }
  };

  const defaultTrigger = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => setOpen(true)}
      className="rounded-full"
      data-testid={`suggest-edit-pool-${pool.pool_id}`}
    >
      <Edit3 className="w-3.5 h-3.5 mr-1" />Suggérer une modification
    </Button>
  );

  return (
    <>
      {trigger
        ? React.cloneElement(trigger, { onClick: () => setOpen(true) })
        : defaultTrigger}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sky-800">
              <Edit3 className="w-5 h-5" />Suggérer une modification
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3" data-testid="pool-suggest-form">
            <p className="text-sm text-slate-600">
              Modifiez les champs incorrects. Votre suggestion sera <strong>vérifiée par un administrateur</strong> avant publication.
            </p>
            <div>
              <Label className="text-sm">Nom de l'établissement</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="pool-suggest-name" />
            </div>
            <div>
              <Label className="text-sm">Adresse</Label>
              <AddressAutocomplete
                value={address}
                onChange={setAddress}
                onSelect={(s) => {
                  setAddress(s.label || s.display_name || address);
                  if (s.lat) setLat(Number(s.lat));
                  if (s.lon) setLng(Number(s.lon));
                }}
                testId="pool-suggest-address"
              />
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Input value={lat} onChange={(e) => setLat(e.target.value)} placeholder="Latitude" data-testid="pool-suggest-lat" />
                <Input value={lng} onChange={(e) => setLng(e.target.value)} placeholder="Longitude" data-testid="pool-suggest-lng" />
              </div>
            </div>
            <div>
              <Label className="text-sm">Téléphone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="01 23 45 67 89" data-testid="pool-suggest-phone" />
            </div>
            <div>
              <Label className="text-sm">Motif <span className="text-slate-400 text-xs">(optionnel)</span></Label>
              <Textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ex: J'y suis passé hier, le numéro a changé."
                data-testid="pool-suggest-reason"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" className="rounded-full" onClick={() => setOpen(false)} disabled={busy}>Annuler</Button>
              <Button type="submit" className="rounded-full bg-sky-700 hover:bg-sky-600 text-white" disabled={busy} data-testid="pool-suggest-submit">
                {busy ? "Envoi…" : "Envoyer la suggestion"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
